/**
 * Org Settings → Usage (ottotester#139 phase 4, #766).
 *
 * The org-facing plan-usage view: how much of each limit the org has
 * consumed this period, and when the period resets. Read-only — it
 * surfaces numbers and changes no enforcement. Billing and plan
 * management moved to OttoCloud (#2331); a hosted org's limits come
 * from the account service, a self-hosted org runs unbounded.
 *
 * Five limit dimensions, mirroring `TIER_LIMITS`, grouped by reset
 * semantics (#1107) into two bordered sections:
 *
 *   "This billing period" — counters that reset each period:
 *   • Platform usage — the period agent-cost ceiling.
 *     NOT an invoice amount (#139): it is a margin guardrail, not a
 *     bill. It was labelled "estimated" until #2346, which stopped being
 *     true once #2334 began recording what the supplier actually
 *     charged. The shared cost-estimate note, under the per-user
 *     breakdown, says how the figure is produced and that the
 *     supplier's bill is the actual cost; the hint keeps only the
 *     guardrail framing. Shown in
 *     dollars, or in AI credits once `CREDITS_ENABLED` is on (#1721) —
 *     one unit, never both. Underneath, a per-user breakdown (#1721)
 *     names who drove the spend: one row per teammate, a
 *     "System / automated" row for automated runs, and "Former user"
 *     for deleted accounts; the rows reconcile to the total.
 *   • Runs — the fair-use monthly run allowance.
 *
 *   "Current totals" — running totals against plan capacity, no reset:
 *   • Test scripts — the customer-facing cap. A live stock.
 *   • Applications — apps under test in the org vs `maxApplications`.
 *   • Users — distinct member accounts across the org's workspaces vs
 *     `maxUserAccounts`.
 *
 * Why the split (#1107): a flat list of five dimensions silently mixes
 * "what resets on the period boundary" (cost, runs) with "what is a
 * running total" (scripts, apps, users). Grouping keeps that legible —
 * the reset footnote no longer has to caveat which rows it applies to.
 * Applications + Users come from `OrgUsageView` fields that already
 * existed on the wire (added by #780 / #1037 PR-A.2 alongside the
 * `maxApplications` / `maxUserAccounts` limits) — #1107 only surfaces
 * them here; it adds no new backend counting.
 *
 * An unbounded dimension (`limit === null` — the `unlimited` tier)
 * renders the word "unlimited", never a number.
 *
 * Gated by `billing.read` (owner / admin / billing) — the same matrix
 * permission the Billing tab uses; the server enforces it on
 * `GET /api/orgs/:orgId/usage` independently.
 */
import { useEffect, useState } from 'react'
import { orgsAPI, type OrgUsageView, type OrgWithRole } from '@/lib/api'
import { useCanI } from '@/hooks/useCanI'
import { CostEstimateNote } from '@/components/bi/CostEstimateNote'

/** Format a cents value as a dollar string. Cost limits + usage are
 * stored in integer cents (the codebase-wide convention); the
 * customer reasons in dollars. Every spend figure on this screen is in
 * dollars (#2490 dropped the AI-credits unit — credits are OttoCloud's now). */
function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/** Render one limit dimension's "used / limit" line. An unbounded
 * dimension (`limit === null`) shows the used value against the word
 * "unlimited"; a bounded one shows "used / limit" with a progress
 * bar. `format` maps a raw number to its display string (identity for
 * counts, cents→dollars for cost). */
function UsageRow({
  label,
  used,
  limit,
  format,
  hint,
}: {
  label: string
  used: number
  limit: number | null
  format: (n: number) => string
  hint?: string
}) {
  // Clamp the bar at 100% — usage can exceed a limit (mid-run
  // overshoot is tolerated by the guard), but a >100% bar would
  // overflow its track.
  const pct =
    limit === null || limit === 0
      ? 0
      : Math.min(100, Math.round((used / limit) * 100))
  const over = limit !== null && limit > 0 && used >= limit

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm tabular-nums">
          <span className={over ? 'text-destructive font-semibold' : 'text-foreground'}>
            {format(used)}
          </span>
          <span className="text-muted-foreground">
            {' / '}
            {limit === null ? 'unlimited' : format(limit)}
          </span>
        </span>
      </div>
      {limit !== null && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} usage`}
        >
          <div
            className={over ? 'h-full bg-destructive' : 'h-full bg-primary'}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** The per-user AI-spend breakdown shown under "Platform usage"
 * (#1721) — who drove this period's spend. Rows arrive already sorted by
 * spend (System pinned last) and already resolved to labels: a teammate's
 * email/name, "System / automated" for automated runs, "Former user" for a
 * deleted account. They reconcile to the platform-usage total above.
 *
 * `format` is the SAME unit formatter the total uses, so a row never shows
 * a different unit than the figure it rolls up into. Non-person rows
 * (System / Former user) are muted so the real teammates read first.
 *
 * A compact label→value list, intentionally simpler than the credit card's
 * bar table (which folds long teams and draws bars) — this sits inside a
 * usage row, not as its own card, so it stays quiet. Kept local rather
 * than shared with that table: the two differ in copy, unit, and layout. */
function PerUserSpendBreakdown({
  rows,
  format,
}: {
  rows: OrgUsageView['perUser']['rows']
  format: (cents: number) => string
}) {
  return (
    <div className="space-y-2 border-l-2 border-muted pl-3">
      <p className="text-xs font-medium text-muted-foreground">Who's using AI this period</p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No AI usage is tied to a person yet this period.
        </p>
      ) : (
        <table className="w-full text-sm tabular-nums">
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.label}-${i}`}>
                <td
                  className={`py-0.5 pr-3 ${row.kind === 'user' ? '' : 'text-muted-foreground'}`}
                >
                  {row.label}
                </td>
                <td className="py-0.5 text-right font-medium">{format(row.costCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Section header for a usage group (#1107). The uppercase label names
 * the group ("This billing period" / "Current totals"); the muted
 * `note` carries the reset semantics for that group (a reset date for
 * period counters, "capacity, does not reset" for running totals). */
function UsageGroupHeader({ label, note }: { label: string; note: string }) {
  // Semantic <h3> (not <p>) — it is a section heading; matches the
  // settings/admin section pattern and reads correctly to screen
  // readers. Tailwind preflight neutralises the default heading
  // margin/size, so the existing classes render identically.
  return (
    <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
      <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
        · {note}
      </span>
    </h3>
  )
}

export function OrgUsage({ org }: { org: OrgWithRole }) {
  const canRead = useCanI('billing.read', org._id)
  const [view, setView] = useState<OrgUsageView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!canRead) {
      setLoading(false)
      return
    }
    setLoading(true)
    // Clear any prior error/view so a fetch that succeeds after an
    // earlier failure (e.g. an org switch) does not keep rendering the
    // stale error, and no previous org's numbers flash through.
    setError(null)
    setView(null)
    orgsAPI
      .getUsage(org._id)
      .then((v) => {
        if (cancelled) return
        setView(v)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load usage')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [org._id, canRead])

  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground">
        Only owners, admins, and billing-role members can view plan usage.
      </p>
    )
  }
  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading usage…</p>
  }
  if (error) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }
  if (!view) return null

  // The period reset date, formatted once and reused by both the
  // "This billing period" group header and the footnote (#1107).
  // periodResetAt is a UTC month boundary — format in UTC so a
  // negative-offset timezone does not show the prior day.
  const resetDate = new Date(view.periodResetAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

  // Every spend figure on this screen is in dollars (#2490). Applied to the
  // platform-usage total AND each per-user row.
  const formatSpend = dollars

  return (
    <div className="max-w-md space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Plan usage</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The figures below show this period's usage ({view.period}) and your
          current totals against your plan limits.
        </p>
      </div>

      {/* Group A — period-resetting dimensions (#1107). Cost + runs
          live on the (orgId, period) counter and zero out at the reset
          boundary; the section header carries that reset date. */}
      <div className="space-y-5 rounded-lg border p-5">
        <UsageGroupHeader label="This period" note={`resets ${resetDate}`} />
        {/* #2346 — "Estimated" left the label because it stopped being
            true: since #2334 some of this figure is exactly what the AI
            supplier charged. The guardrail framing stays, because that is
            still what the number is for.

            How the figure is produced, and where the actual cost lives,
            is said by the cost-estimate note under the per-person list,
            the same note the analytics screens show. It describes the
            rule, not the outcome: "some billed, the rest worked out"
            would be false for a period that was all one or the other,
            and nothing here can tell which. So the hint keeps only what
            the number is for. */}
        <UsageRow
          label="Platform usage"
          used={view.cost.used}
          limit={view.cost.limit}
          format={formatSpend}
          hint="Agent (AI) spend this period, a usage guardrail rather than an invoice amount."
        />
        <PerUserSpendBreakdown rows={view.perUser.rows} format={formatSpend} />
        <CostEstimateNote />
        <UsageRow
          label="Runs"
          used={view.runs.used}
          limit={view.runs.limit}
          format={(n) => String(n)}
          hint="Authoring and replay runs started this period."
        />
      </div>

      {/* Group B — running totals against plan capacity (#1107). These
          are live stocks (scripts, apps, distinct member accounts), not
          period counters, so they do NOT reset. */}
      <div className="space-y-5 rounded-lg border p-5">
        <UsageGroupHeader label="Current totals" note="capacity, does not reset" />
        <UsageRow
          label="Test scripts"
          used={view.scripts.used}
          limit={view.scripts.limit}
          format={(n) => String(n)}
          hint="The total number of test scripts in this organization."
        />
        <UsageRow
          label="Applications"
          used={view.applications.used}
          limit={view.applications.limit}
          format={(n) => String(n)}
          hint="Applications under test in this organization."
        />
        {/* The wire field is `userAccounts`; the user-facing label is
            "Users" (#1107) — distinct member accounts across the org's
            workspaces, the value `maxUserAccounts` caps. */}
        <UsageRow
          label="Users"
          used={view.userAccounts.used}
          limit={view.userAccounts.limit}
          format={(n) => String(n)}
          hint="Member accounts across all workspaces in this organization."
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Platform usage and runs reset on{' '}
        <span className="font-medium text-foreground">{resetDate}</span>. Test
        scripts, applications, and users are running totals against your plan
        capacity and do not reset.
      </p>
    </div>
  )
}
